import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { from, switchMap } from 'rxjs';
import { ProductWaitinglistPriorityRule } from '../entities/ProductWaitinglistPriorityRule.js';
import { Product } from '../entities/products.js';

async function getProductWaitinglistPriorityRulesQueryForProduct(
  productID: string,
  ignoreInstitutionRules: boolean = false,
) {
  const product = await getByID(
    Product,
    productID,
  )({ institutionID: 1, overrideInstitutionWaitinglistPriorityRules: 1 });
  if (!product) throw new Error('product not found ' + productID);
  const institutionID = product.institutionID;

  const query = (() => {
    if (product.overrideInstitutionWaitinglistPriorityRules || ignoreInstitutionRules) return { productID } as const;
    return { productID: undefined, institutionID } as const;
  })();

  return query;
}

export async function getWaitinglistPriorityForUserForProduct(userID: string, productID: string) {
  const rulesQuery = await getProductWaitinglistPriorityRulesQueryForProduct(productID);
  const rulesData = await loadMongoData(ProductWaitinglistPriorityRule, rulesQuery);
  const priorityRules = await getByID(
    ProductWaitinglistPriorityRule,
    rulesData.map(r => r._id),
  )(selectionSet(() => ProductWaitinglistPriorityRule, 3));

  if (priorityRules.length === 0) return 'high' as const; // use high as default when no rules are found

  for (const priorityRule of priorityRules) {
    const hasHighPriority = await priorityRule.hasHighPriority(userID);
    if (hasHighPriority) return 'high' as const;
  }

  return 'low' as const;
}

export const subscribeProductWaitinglistPriorityRulesForProduct = registerSubscription({
  operationName: 'subscribeProductWaitinglistPriorityRulesForProduct',
  params: [
    { name: 'productID', type: () => ID },
    { name: 'ignoreInstitutionRules', type: () => Boolean },
  ],
  type: () => [ProductWaitinglistPriorityRule],
  resolve:
    (productID: string, ignoreInstitutionRules: boolean = false) =>
    <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) => {
      const queryPromise = getProductWaitinglistPriorityRulesQueryForProduct(productID, ignoreInstitutionRules);
      return from(queryPromise).pipe(
        switchMap(query => {
          return watchQuery<ProductWaitinglistPriorityRule>(ProductWaitinglistPriorityRule._collectionName, query);
        }),
      );
    },
});

export const subscribeProductWaitinglistPriorityRulesForInstitution = registerSubscription({
  operationName: 'subscribeProductWaitinglistPriorityRulesForInstitution',
  params: [{ name: 'institutionID', type: () => ID }],
  type: () => [ProductWaitinglistPriorityRule],
  resolve:
    (institutionID: string) =>
    <SelSet extends BaseQLSelectionSet<ProductWaitinglistPriorityRule>>(gqlFields: SelSet) =>
      watchQuery<ProductWaitinglistPriorityRule>(ProductWaitinglistPriorityRule._collectionName, {
        institutionID,
        productID: null,
      }),
});
